import { useCallback, useEffect, useRef, useState } from 'react';

const defaultOptions = {
  immediate: true,
};

const useAsync = (asyncFunction, options = defaultOptions) => {
  const { immediate } = { ...defaultOptions, ...options };
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(!!immediate);
  const [hasFetched, setHasFetched] = useState(false);

  const mountedRef = useRef(false);
  const immediateFiredRef = useRef(false);

  const execute = useCallback(
    async (...args) => {
      if (mountedRef.current) {
        setLoading(true);
        setError(null);
      }
      try {
        const result = await asyncFunction(...args);
        if (mountedRef.current) {
          setData(result);
          setHasFetched(true);
        }
        return result;
      } catch (asyncError) {
        if (mountedRef.current) {
          setError(asyncError);
        }
        throw asyncError;
      } finally {
        if (mountedRef.current) {
          setLoading(false);
        }
      }
    },
    [asyncFunction]
  );

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  const prevExecuteRef = useRef(null);

  useEffect(() => {
    // Reset guard when execute identity changes (real dependency change),
    // but NOT on StrictMode unmount/remount (same execute identity).
    if (prevExecuteRef.current !== execute) {
      immediateFiredRef.current = false;
      prevExecuteRef.current = execute;
    }

    if (immediate) {
      if (immediateFiredRef.current) return;
      immediateFiredRef.current = true;
      execute();
    }
  }, [execute, immediate]);

  return {
    data,
    error,
    loading,
    hasFetched,
    isIdle: !loading && !hasFetched && !error,
    execute,
  };
};

export default useAsync;
